/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { useEffect, useState } from "react";
import {
  List,
  IconButton,
  Collapse,
  Breadcrumbs,
  Link,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "components/generals/Grid";
import TextField from "@mui/material/TextField";
import ListItem from "components/generals/ListItem";
import Box from "components/generals/Box";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Typography from "components/generals/Typography";
import FilesViewer from "components/generals/MedicalAssetViewer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Card from "components/generals/Card";
import { DocumentFolder } from "interfaces";
import { AssetInterface, MediaCategoryEnum, MediaMimeTypesEnum } from "medicalsystem-shared";
import Dropzone from "components/generals/MedicalAssetDropzone";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import { useTranslation } from "react-i18next";

/**
 * Properties for the DocumentViewer component.
 * 
 * @interface FetchDocuments
 * @property {DocumentFolder[]} folders - Document folders with categorized files
 * @property {string} [patientId] - Patient ID for file management
 * @property {Function} setIsDataUpdated - Callback to signal data changes
 */
interface FetchDocuments {
  folders: DocumentFolder[];
  patientId?: string;
  setIsDataUpdated: (updated: boolean) => void;
}

/**
 * Document viewer component for managing and viewing patient documents.
 * Provides hierarchical folder structure with file upload and preview capabilities.
 * 
 * Features:
 * - Folder-based document organization by category
 * - Collapsible folder structure
 * - Breadcrumb navigation
 * - File type icons (PDF, Image, Document)
 * - File upload with drag-and-drop
 * - File preview in modal viewer
 * - Create custom folders
 * - Category grouping (Test Results, Clinic Documents, Other)
 * - Permission-based upload control
 * - Responsive card layout
 * - Real-time document updates
 * - i18n translation support
 * 
 * Document Categories:
 * - TEST_RESULTS: Lab tests, imaging results
 * - CLINIC_DOCUMENT: Clinical documents
 * - OTHER: Miscellaneous documents
 * - Custom categories created by user
 * 
 * File Management:
 * - Upload to specific folders
 * - Group by category
 * - View file details
 * - Preview in FilesViewer component
 * - Support for PDF, images, and documents
 * 
 * Navigation:
 * - Breadcrumb trail showing folder path
 * - Click folder to expand/collapse
 * - Click file to preview
 * - Back to root with breadcrumb click
 * 
 * Upload Flow:
 * 1. Select or create folder
 * 2. Drag or select files
 * 3. Files uploaded to category
 * 4. View refreshed automatically
 * 
 * @param {FetchDocuments} props - Component properties
 * @returns {JSX.Element} Document viewer with folder structure
 */
const DocumentViewer: React.FC<FetchDocuments> = ({ folders, patientId, setIsDataUpdated }) => {
  const [selectedFile, setSelectedFile] = useState<AssetInterface>();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState<string[]>([]);
  const [groupedFolders, setGroupedFolders] = useState<Record<string, AssetInterface[]>>({});
  const [open, setOpen] = useState(false);
  const [categoryName, setCategoryName] = useState<string>("");
  const { t } = useTranslation();
  useEffect(() => {
    const defaultCategories = [
      MediaCategoryEnum.TEST_RESULTS,
      MediaCategoryEnum.CLINIC_DOCUMENT,
      MediaCategoryEnum.OTHER,
    ];

    const initialGroupedFolders = defaultCategories.reduce(
      (acc, category) => {
        if (!acc[category]) {
          acc[category] = [];
        }
        return acc;
      },
      {} as Record<string, AssetInterface[]>
    );

    folders.forEach((folder) => {
      const folderName = folder.folderName;
      if (!initialGroupedFolders[folderName]) {
        initialGroupedFolders[folderName] = [];
      }
      initialGroupedFolders[folderName] = [
        ...initialGroupedFolders[folderName],
        ...folder.documents,
      ];
    });

    const otherCategory = initialGroupedFolders[MediaCategoryEnum.OTHER];
    delete initialGroupedFolders[MediaCategoryEnum.OTHER];

    setGroupedFolders({
      ...initialGroupedFolders,
      [MediaCategoryEnum.OTHER]: otherCategory,
    });
  }, [folders]);

  useEffect(() => {
    if (openCategory || openCategory == null) {
      setSelectedFile(null);
    }
  }, [openCategory]);

  const getFileIcon = (fileName: string) => {
    switch (fileName) {
      case MediaMimeTypesEnum.DOCUMENT_PDF:
        return <PictureAsPdfIcon />;
      case MediaMimeTypesEnum.IMAGE_PNG:
      case MediaMimeTypesEnum.IMAGE_JPG:
      case MediaMimeTypesEnum.IMAGE_PJPEG:
        return <ImageIcon />;
      case MediaMimeTypesEnum.DOCUMENT_DOC:
      case MediaMimeTypesEnum.DOCUMENT_DOCX:
        return <DescriptionIcon />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const handleFileClick = (file: AssetInterface) => {
    setSelectedFile(file);
  };

  const handleToggle = (category: string) => {
    setOpenCategory(openCategory === category ? null : category);
    setBreadcrumbPath([category]);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(event.target.value);
  };

  const handleSave = () => {
    if (!categoryName) return;

    setGroupedFolders((prevFolders) => ({
      ...prevFolders,
      [categoryName]: [],
    }));

    handleClose();
    setCategoryName("");
  };

  return (
    <>
      <Card>
        <Box ml={2} mt={2}>
          <Grid mb={2}>
            <Typography variant="h3">{t("documentViewer.documentViewer")}</Typography>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={4} lg={4}>
              {Object.keys(groupedFolders).map((folderName, index) => (
                <List key={index} component="nav">
                  <ListItem
                    id={"cy-folder-"+folderName}
                    className={openCategory === folderName ? "folder-item-selected" : "folder-item"}
                    onClick={() => handleToggle(folderName)}
                  >
                    <IconButton>
                      {openCategory === folderName ? <FolderOpenIcon /> : <FolderIcon />}
                      <Typography ml={1} variant="h6">
                        {t(`documentViewer.${folderName}`)}
                      </Typography>
                    </IconButton>
                  </ListItem>

                  <Collapse in={openCategory === folderName} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {groupedFolders[folderName].map((file, fileIndex) => (
                        <ListItem
                          key={fileIndex}
                          onClick={() => handleFileClick(file)}
                          className="file-item"
                        >
                          <ListItemIcon sx={{ pl: 2 }}>{getFileIcon(file.mimetype)}</ListItemIcon>
                          <Typography variant="caption" color="text" fontWeight="medium">
                            {file.orgName}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </List>
              ))}
              <Grid mt={2} mb={2}>
                <PermissionWrapper
                  requiredPermissions={[
                    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,
                  ]}
                >
                  <Button variant="contained" color="primary" onClick={handleClickOpen}>
                    {t("documentViewer.addNewCategory")}
                  </Button>
                </PermissionWrapper>
              </Grid>
            </Grid>
            <Grid item xs={12} md={8} lg={8} pl={1} pr={1} pb={1}>
              {openCategory != null && (
                <>
                  <Grid mb={2}>
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                      <Link
                        underline="hover"
                        key="1"
                        color="inherit"
                        variant="caption"
                        fontWeight="medium"
                      >
                        <Typography variant="caption" fontWeight="regular">
                          / {t("documentViewer.documentViewer")}
                        </Typography>
                      </Link>
                      ,
                      {breadcrumbPath.map((crumb, idx) => (
                        <Typography key="2" variant="caption" fontWeight="medium">
                          {crumb}
                        </Typography>
                      ))}
                    </Breadcrumbs>
                  </Grid>
                  <Grid mb={2}>
                    <Dropzone
                      openedCategory={openCategory}
                      patientId={patientId}
                      setIsDataUpdated={setIsDataUpdated}
                    />
                  </Grid>
                </>
              )}
              {selectedFile ? (
                <FilesViewer key={selectedFile.id} asset={selectedFile} />
              ) : (
                <Typography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  textAlign={"center"}
                  display={"block"}
                >
                  {t("documentViewer.selectDocumentPreview")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t("documentViewer.addNewCategory")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("documentViewer.DialogContentText")}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="category-name"
            label={t("documentViewer.categoryName")}
            type="text"
            fullWidth
            variant="standard"
            value={categoryName}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t("documentViewer.cancel")}
          </Button>
          <Button onClick={handleSave} color="primary">
            {t("documentViewer.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentViewer;
